import React, { useEffect, useState } from "react";
import BreadCrumb from "../components/BreadCrumb";
import Meta from "../components/Meta";
import watch from "../images/watch.jpg";
import { AiFillDelete, AiOutlineShoppingCart } from "react-icons/ai";
import { BsArrowLeft, BsTruck, BsShieldCheck, BsCreditCard, BsCheckCircle, BsCalendarEvent } from "react-icons/bs";
import { Link, useNavigate } from "react-router-dom";
import Container from "../components/Container";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteCartProduct,
  getUserCart,
  updateCartProduct,
} from "../features/user/userSlice";
import { trackViewContent, trackAddToCart, trackInitiateCheckout } from "../utils/metaPixel";

const Cart = () => {
  const getTokenFromLocalStorage = localStorage.getItem("customer")
    ? JSON.parse(localStorage.getItem("customer"))
    : null;

  const config2 = {
    headers: {
      Authorization: `Bearer ${
        getTokenFromLocalStorage !== null ? getTokenFromLocalStorage.token : ""
      }`,
      Accept: "application/json",
    },
  };

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [productupdateDetail, setProductupdateDetail] = useState(null);
  const [totalAmount, setTotalAmount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasPreorderItems, setHasPreorderItems] = useState(false);
  const userCartState = useSelector((state) => state.auth.cartProducts);
  const guestCartItems = useSelector((state) => state.auth.guestCart);

  // Check if user is authenticated by verifying token exists and is likely valid
  const isAuthenticated = () => {
    if (!getTokenFromLocalStorage || !getTokenFromLocalStorage.token) {
      return false;
    }
    // Additional check could be added here for token expiration
    return true;
  };

  useEffect(() => {
    // For guest users, check localStorage for guest cart items
    const customer = localStorage.getItem("customer");
    if (!customer) {
      // For guest users, fetch product details for items in the guest cart
      dispatch(getUserCart());
    } else {
      dispatch(getUserCart(config2));
    }
  }, []);

  useEffect(() => {
    if (productupdateDetail !== null) {
      setLoading(true);
      dispatch(
        updateCartProduct({
          cartItemId: productupdateDetail?.cartItemId,
          quantity: productupdateDetail?.quantity,
        })
      );
      setTimeout(() => {
        if (isAuthenticated()) {
          dispatch(getUserCart(config2));
        } else {
          dispatch(getUserCart()); // For guest users
        }
        setLoading(false);
      }, 200);
    }
  }, [productupdateDetail]);

  const deleteACartProduct = (id) => {
    setLoading(true);
    
    // Get the item being deleted to track the event
    const cartItems = getCurrentCartItems();
    const itemToDelete = cartItems.find(item => {
      if (!isAuthenticated()) {
        return item.tempId === id;
      } else {
        return item._id === id;
      }
    });
    
    if (itemToDelete) {
      const product = isAuthenticated() ? itemToDelete.productId : itemToDelete.productDetails;
      if (product) {
        // Track as removing from cart (negative add to cart event)
        trackAddToCart(product, -itemToDelete.quantity);
      }
    }
    
    dispatch(deleteCartProduct({ id: id, config2: config2 }));
    setTimeout(() => {
      if (isAuthenticated()) {
        dispatch(getUserCart(config2));
      } else {
        dispatch(getUserCart()); // For guest users
      }
      setLoading(false);
    }, 200);
  };

  useEffect(() => {
    let sum = 0;
    let preorder = false;
    
    if (!isAuthenticated()) {
      // Calculate totals for guest cart
      if (guestCartItems && Array.isArray(guestCartItems)) {
        for (let index = 0; index < guestCartItems.length; index++) {
          const item = guestCartItems[index];
          // Use discounted price if available, otherwise use regular price
          const discountedPrice = item?.productDetails?.discountedPrice;
          const price = item?.productDetails?.price || item?.price || 0;
          const finalPrice = discountedPrice || price;
          sum = sum + Number(item.quantity) * finalPrice;
          
          // Check if any items are preorder items
          if (item.productDetails?.isPreorder) {
            preorder = true;
          }
        }
      }
    } else {
      // Calculate totals for authenticated user cart
      if (userCartState && Array.isArray(userCartState)) {
        for (let index = 0; index < userCartState.length; index++) {
          // Use discounted price if available, otherwise use regular price
          const discountedPrice = userCartState[index]?.productId?.discountedPrice;
          const price = userCartState[index].price;
          const finalPrice = discountedPrice || price;
          sum = sum + Number(userCartState[index].quantity) * finalPrice;
          
          // Check if any items are preorder items
          if (userCartState[index]?.productId?.isPreorder) {
            preorder = true;
          }
        }
      }
    }
    
    setTotalAmount(sum);
    setHasPreorderItems(preorder);
  }, [userCartState, guestCartItems]);

  const updateQuantity = (cartItemId, newQuantity) => {
    if (newQuantity >= 1 && newQuantity <= 10) {
      // Get the current item to track the change
      const cartItems = getCurrentCartItems();
      const currentItem = cartItems.find(item => {
        if (!isAuthenticated()) {
          return item.tempId === cartItemId;
        } else {
          return item._id === cartItemId;
        }
      });
      
      if (currentItem) {
        const product = isAuthenticated() ? currentItem.productId : currentItem.productDetails;
        if (product) {
          // Track the quantity change
          const quantityChange = newQuantity - currentItem.quantity;
          if (quantityChange !== 0) {
            trackAddToCart(product, quantityChange);
          }
        }
      }
      
      setProductupdateDetail({
        cartItemId: cartItemId,
        quantity: newQuantity,
      });
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Check if cart is empty (for both guest and authenticated users)
  const isCartEmpty = () => {
    if (!isAuthenticated()) {
      return !guestCartItems || guestCartItems.length === 0;
    } else {
      return !userCartState || userCartState.length === 0;
    }
  };

  // Track cart view when cart is not empty - only once when component mounts
  useEffect(() => {
    if (!isCartEmpty() && getCurrentCartItems()) {
      // Track each item in the cart as viewed content
      getCurrentCartItems().forEach(item => {
        const product = isAuthenticated() ? item.productId : item.productDetails;
        if (product) {
          trackViewContent({
            ...product,
            category: product.category || "Cart"
          });
        }
      });
    }
  }, []); // Empty dependency array to run only once on mount

  if (isCartEmpty()) {
    return (
      <>
        <Meta 
          title="Your Shopping Cart - VEDA Merchandise"
          description="View and manage items in your shopping cart at VEDA Merchandise."
          keywords="shopping cart, cart, VEDA cart, mythological toys cart"
          author="VEDA Merchandise"
          robots="noindex, nofollow"
        />
        <div className="min-h-screen bg-gradient-to-br from-red-50 via-yellow-50 to-red-50 py-12 px-4">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-3xl shadow-xl p-12 text-center">
              <div className="inline-flex items-center justify-center w-24 h-24 bg-red-100 rounded-full mb-6">
                <AiOutlineShoppingCart className="text-red-500 text-5xl" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-3">Your cart is empty</h2>
              <p className="text-gray-600 mb-8 text-lg">
                Looks like you haven't added any products to your cart yet.
              </p>
              <Link 
                to="/products" 
                className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-red-500 to-red-600 text-white font-bold rounded-full shadow-lg hover:from-red-600 hover:to-red-700 transition-all transform hover:scale-105"
              >
                <BsArrowLeft className="text-xl" />
                <span>Continue Shopping</span>
              </Link>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Schema markup for structured data
  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Shopping Cart - VEDA Merchandise",
    "description": "View and manage items in your shopping cart at VEDA Merchandise.",
    "url": "https://vedamerchandise.com/cart",
    "publisher": {
      "@type": "Organization",
      "name": "VEDA Merchandise",
      "logo": {
        "@type": "ImageObject",
        "url": "https://vedamerchandise.com/logo.svg"
      }
    }
  };

  // Get the current cart items (either guest or authenticated user)
  const getCurrentCartItems = () => {
    if (!isAuthenticated()) {
      return guestCartItems;
    } else {
      return userCartState;
    }
  };

  // Check if any item in the cart has the 'comingsoon' tag
  const hasComingSoonItems = () => {
    const cartItems = getCurrentCartItems();
    if (!cartItems || !Array.isArray(cartItems)) {
      return false;
    }
    
    return cartItems.some(item => {
      if (!isAuthenticated()) {
        // Guest user item
        return item?.productDetails?.tags?.includes("comingsoon");
      } else {
        // Authenticated user item
        return item?.productId?.tags?.includes("comingsoon");
      }
    });
  };

  // Get product image for a cart item
  const getProductImage = (item) => {
    if (!isAuthenticated()) {
      // Guest user item
      return item?.productDetails?.images?.[0]?.url || watch;
    } else {
      // Authenticated user item
      return item?.productId?.images?.[0]?.url || watch;
    }
  };

  // Get product title for a cart item
  const getProductTitle = (item) => {
    if (!isAuthenticated()) {
      // Guest user item
      return item?.productDetails?.title || "Product Title";
    } else {
      // Authenticated user item
      return item?.productId?.title || "Product Title";
    }
  };

  // Get product brand for a cart item
  const getProductBrand = (item) => {
    if (!isAuthenticated()) {
      // Guest user item
      return item?.productDetails?.brand || "Unknown";
    } else {
      // Authenticated user item
      return item?.productId?.brand || "Unknown";
    }
  };

  // Check if product is preorder for a cart item
  const isProductPreorder = (item) => {
    if (!isAuthenticated()) {
      // Guest user item
      return item?.productDetails?.isPreorder || false;
    } else {
      // Authenticated user item
      return item?.productId?.isPreorder || false;
    }
  };

  // Get preorder release date for a cart item
  const getPreorderReleaseDate = (item) => {
    if (!isAuthenticated()) {
      // Guest user item
      return item?.productDetails?.preorderReleaseDate || null;
    } else {
      // Authenticated user item
      return item?.productId?.preorderReleaseDate || null;
    }
  };

  // Get item price for a cart item
  const getItemPrice = (item) => {
    if (!isAuthenticated()) {
      // Guest user item - use discounted price if available
      const discountedPrice = item?.productDetails?.discountedPrice;
      const price = item?.productDetails?.price || item?.price || 0;
      return discountedPrice || price;
    } else {
      // Authenticated user item - use discounted price if available
      const discountedPrice = item?.productId?.discountedPrice;
      const price = item?.price || 0;
      return discountedPrice || price;
    }
  };

  // Get item ID for a cart item
  const getItemId = (item) => {
    if (!isAuthenticated()) {
      // Guest user item
      return item?.tempId;
    } else {
      // Authenticated user item
      return item?._id;
    }
  };

  // Get item quantity for a cart item
  const getItemQuantity = (item) => {
    return item?.quantity || 1;
  };

  return (
    <>
      <Meta 
        title="Your Shopping Cart - VEDA Merchandise"
        description="View and manage items in your shopping cart at VEDA Merchandise."
        keywords="shopping cart, cart, VEDA cart, mythological toys cart"
        author="VEDA Merchandise"
        ogTitle="Your Shopping Cart - VEDA Merchandise"
        ogDescription="View and manage items in your shopping cart at VEDA Merchandise."
        ogImage="https://vedamerchandise.com/cart-preview.jpg"
        ogUrl="https://vedamerchandise.com/cart"
        ogType="website"
        twitterCard="summary_large_image"
        twitterTitle="Your Shopping Cart - VEDA Merchandise"
        twitterDescription="View and manage items in your shopping cart at VEDA Merchandise."
        twitterImage="https://vedamerchandise.com/cart-preview.jpg"
        robots="noindex, nofollow"
        canonicalUrl="https://vedamerchandise.com/cart"
        schemaMarkup={schemaMarkup}
      />
      <div className="min-h-screen py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            
            {/* Cart Items Section */}
            <div className="lg:col-span-2">
              {/* Header */}
              <div className="mb-6">
                <span className="my-3 text-3xl bg-yellow-200 text-gray-800 py-2 px-6 rounded-full font-bold">Shopping Cart</span>
                <p className="text-gray-600 font-semibold mt-2 ml-4">
                  {getCurrentCartItems()?.length || 0} item{getCurrentCartItems()?.length !== 1 ? 's' : ''} in your cart
                </p>
                
                {/* Preorder Notice */}
                {hasPreorderItems && (
                  <div className="mt-4 p-4 bg-yellow-600 bg-opacity-10 border rounded-3xl">
                    <div className="flex items-start gap-3">
                      <BsCalendarEvent className=" mt-1 flex-shrink-0" size={20} />
                      <div>
                        <h3 className="font-bold mb-1">Preorder Items in Your Cart</h3>
                        <p className="text-sm ">
                          Your cart contains preorder items. Payment will be processed in full when all items are in stock and ready to ship.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Cart Items */}
              <div className="space-y-4">
                {getCurrentCartItems() &&
                  getCurrentCartItems()?.map((item, index) => {
                    return (
                      <div
                        key={index}
                        className="bg-white rounded-3xl shadow-lg border-2 border-gray-100 p-6 hover:shadow-xl transition-shadow"
                      >
                        <div className="flex flex-col md:flex-row gap-4">
                          {/* Product Image */}
                          <div className="flex-shrink-0 relative">
                            <img
                              src={getProductImage(item)}
                              className="w-full md:w-32 h-32 object-cover rounded-2xl"
                              alt={getProductTitle(item)}
                            />
                            {/* Preorder Badge */}
                            {isProductPreorder(item) && (
                              <span className="absolute top-2 right-2 bg-[#e91e63] text-white text-xs font-bold px-2 py-1 rounded-full">
                                Preorder
                              </span>
                            )}
                          </div>

                          {/* Product Details */}
                          <div className="flex-grow">
                            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                              <div className="flex-grow">
                                <h3 className="text-lg font-bold text-gray-900 mb-2">
                                  {getProductTitle(item)}
                                </h3>
                                <p className="text-sm text-gray-600 mb-3">
                                  Brand: <span className="font-semibold">{getProductBrand(item)}</span>
                                </p>
                                
                                {/* Preorder Info */}
                                {isProductPreorder(item) && (
                                  <div className="mb-3">
                                    <span className="inline-block bg-[#e91e63]  text-white text-xs px-2 py-1 rounded-full">
                                      Preorder Item
                                    </span>
                                    {getPreorderReleaseDate(item) && (
                                      <p className="text-xs text-gray-500 mt-1">
                                        Estimated Release: {formatDate(getPreorderReleaseDate(item))}
                                      </p>
                                    )}
                                  </div>
                                )}
                                
                                {/* Display discounted price and original price with discount percentage */}
                                {isAuthenticated() ? (
                                  item?.productId?.discountedPrice && item?.productId?.originalPrice && item?.productId?.discountedPrice < item?.productId?.originalPrice ? (
                                    <div className="d-flex align-items-center gap-2">
                                      <p className="text-xl font-bold text-red-500 mb-0">
                                        ₹{item?.productId?.discountedPrice?.toLocaleString()}
                                      </p>
                                      <p className="text-sm text-gray-500 font-normal mb-0 line-through">
                                        ₹{item?.productId?.originalPrice?.toLocaleString()}
                                      </p>
                                      <span className="badge bg-danger fs-6 px-2 py-1 rounded-pill">
                                        {Math.round(((item?.productId?.originalPrice - item?.productId?.discountedPrice) / item?.productId?.originalPrice) * 100)}% OFF
                                      </span>
                                      <span className="text-sm text-gray-500 font-normal ml-2">per item</span>
                                    </div>
                                  ) : (
                                    <p className="text-xl font-bold text-red-500">
                                      ₹{getItemPrice(item)?.toLocaleString()}
                                      <span className="text-sm text-gray-500 font-normal ml-2">per item</span>
                                    </p>
                                  )
                                ) : item?.productDetails?.discountedPrice && item?.productDetails?.originalPrice && item?.productDetails?.discountedPrice < item?.productDetails?.originalPrice ? (
                                  <div className="d-flex align-items-center gap-2">
                                    <p className="text-xl font-bold text-red-500 mb-0">
                                      ₹{item?.productDetails?.discountedPrice?.toLocaleString()}
                                    </p>
                                    <p className="text-sm text-gray-500 font-normal mb-0 line-through">
                                      ₹{item?.productDetails?.originalPrice?.toLocaleString()}
                                    </p>
                                    <span className="badge bg-danger fs-6 px-2 py-1 rounded-pill">
                                      {Math.round(((item?.productDetails?.originalPrice - item?.productDetails?.discountedPrice) / item?.productDetails?.originalPrice) * 100)}% OFF
                                    </span>
                                    <span className="text-sm text-gray-500 font-normal ml-2">per item</span>
                                  </div>
                                ) : (
                                  <p className="text-xl font-bold text-red-500">
                                    ₹{getItemPrice(item)?.toLocaleString()}
                                    <span className="text-sm text-gray-500 font-normal ml-2">per item</span>
                                  </p>
                                )}
                              </div>

                              {/* Quantity & Actions */}
                              <div className="flex flex-row md:flex-col items-center md:items-end gap-4">
                                {/* Quantity Controls */}
                                <div className="flex items-center gap-2">
                                  <button 
                                    className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors disabled:opacity-50"
                                    onClick={() => {
                                      updateQuantity(getItemId(item), Math.max(1, (getItemQuantity(item) - 1)));
                                    }}
                                    disabled={loading || getItemQuantity(item) <= 1}
                                  >
                                    <span className="text-lg font-bold">-</span>
                                  </button>
                                  <input
                                    className="w-16 h-10 text-center border-2 border-gray-200 rounded-xl font-bold focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                    type="number"
                                    min={1}
                                    max={10}
                                    value={getItemQuantity(item)}
                                    onChange={(e) => {
                                      const newQuantity = parseInt(e.target.value) || 1;
                                      updateQuantity(getItemId(item), newQuantity);
                                    }}
                                    disabled={loading}
                                  />
                                  <button 
                                    className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors disabled:opacity-50"
                                    onClick={() => {
                                      updateQuantity(getItemId(item), Math.min(10, (getItemQuantity(item) + 1)));
                                    }}
                                    disabled={loading || getItemQuantity(item) >= 10}
                                  >
                                    <span className="text-lg font-bold">+</span>
                                  </button>
                                </div>

                                {/* Remove Button */}
                                <button
                                  className="flex items-center gap-2 px-4 py-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full transition-colors font-semibold"
                                  onClick={() => {
                                    deleteACartProduct(getItemId(item));
                                  }}
                                  disabled={loading}
                                >
                                  <AiFillDelete className="text-xl" />
                                  <span className="hidden sm:inline">Remove</span>
                                </button>
                              </div>
                            </div>

                            {/* Item Total */}
                            <div className="mt-4 pt-4 border-t-2 border-gray-100">
                              <div className="flex justify-between items-center">
                                <span className="text-gray-600 font-medium">Item Total:</span>
                                <span className="text-2xl font-bold text-gray-900">
                                  ₹{(getItemQuantity(item) * getItemPrice(item)).toLocaleString()}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>

            {/* Order Summary Section */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-3xl shadow-xl border-2 border-gray-100 p-6 sticky top-24">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Order Summary</h3>
                
                {/* Summary Details */}
                <div className="space-y-4 mb-6">
                  <div className="flex justify-between items-center pb-3 border-b-2 border-gray-100">
                    <span className="text-gray-600">Subtotal ({getCurrentCartItems()?.length || 0} items)</span>
                    <span className="font-bold text-gray-900">₹{totalAmount?.toLocaleString() || 0}</span>
                  </div>
                  <div className="flex justify-between items-center pb-3 border-b-2 border-gray-100">
                    <span className="text-gray-600">Shipping</span>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-gray-900 line-through">₹150</span>
                      <span className="font-bold text-green-600">₹0</span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center pb-3 border-b-2 border-gray-100">
                    <span className="text-gray-600">Tax</span>
                    <span className="text-sm text-gray-500">At checkout</span>
                  </div>
                  <div className="flex justify-between items-center pt-2">
                    <span className="text-xl font-bold text-gray-900">Total</span>
                    <span className="text-2xl font-bold text-red-500"> ₹{totalAmount?.toLocaleString() || 0}</span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="space-y-3 mb-6">
                  <button 
                    onClick={() => {
                      // Track initiate checkout event
                      trackInitiateCheckout(getCurrentCartItems(), totalAmount);
                      navigate("/checkout");
                    }}
                    disabled={hasComingSoonItems()}
                    className={`w-full flex items-center justify-center gap-2 px-6 py-4 text-white font-bold rounded-full shadow-lg transition-all transform hover:scale-105 ${hasComingSoonItems() ? 'bg-gray-400 cursor-not-allowed opacity-60' : 'bg-[#e91e63] hover:from-red-600 hover:to-red-700'}`}
                  >
                    <BsCreditCard className="text-xl" />
                    <span>{hasPreorderItems ? "Proceed to Preorder" : hasComingSoonItems() ? "Coming Soon Items" : "Proceed to Checkout"}</span>
                  </button>
                  <Link 
                    to="/products" 
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-full hover:bg-gray-50 transition-colors"
                  >
                    <BsArrowLeft />
                    <span>Continue Shopping</span>
                  </Link>
                </div>

                {/* Additional Info */}
                <div className="space-y-3 p-4 bg-gradient-to-br from-yellow-50 to-red-50 rounded-2xl">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-white rounded-full">
                      <BsTruck className="text-red-500" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">Free Shipping</p>
                      <p className="text-xs text-gray-600">On all orders</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-white rounded-full">
                      <BsShieldCheck className="text-red-500" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">Secure Checkout</p>
                      <p className="text-xs text-gray-600">100% protected</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-white rounded-full">
                      <BsCheckCircle className="text-red-500" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">Easy Returns</p>
                      <p className="text-xs text-gray-600">30-day policy</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Cart;